import { useState, useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";
import type { AppRole } from "@/contexts/RoleContext";
import { buildDefaultPermissions } from "@/lib/fieldPermissionDefaults";

interface Permission {
  can_see: boolean;
  can_edit: boolean;
}

type PermissionMap = Record<string, Permission>;

// Cache en mémoire partagé entre les instances du hook (durée de session)
const cache: Partial<Record<AppRole, PermissionMap>> = {};

// Listeners abonnés aux changements de version
let cacheVersion = 0;
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return cacheVersion;
}

/** Vide le cache pour forcer un re-fetch depuis Supabase au prochain render */
export function invalidateFieldPermissionsCache() {
  for (const key of Object.keys(cache)) delete cache[key as AppRole];
  cacheVersion++;
  listeners.forEach((cb) => cb());
}

export function useFieldPermissions(role: AppRole) {
  const version = useSyncExternalStore(subscribe, getSnapshot);
  const alreadyCached = !!cache[role];
  const [permissions, setPermissions] = useState<PermissionMap>(
    cache[role] ?? {}
  );
  // isLoading = true uniquement pour les rôles non-admin sans cache
  const [isLoading, setIsLoading] = useState(role !== "admin" && !alreadyCached);

  useEffect(() => {
    // Admin bypass — pas besoin de charger depuis Supabase
    if (role === "admin") return;
    // Déjà en cache
    if (cache[role]) {
      setPermissions(cache[role]!);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    supabase
      .from("field_permissions")
      .select("field_key, can_see, can_edit")
      .eq("role", role)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[useFieldPermissions]", error.message);
          setIsLoading(false);
          return;
        }
        // Spec : on part toujours des defaults (couvre les champs ajoutes
        // recemment au registre `BIEN_SECTIONS` mais pas encore upsertes en DB,
        // p.ex. mission PEB 2026-05-23). Les rows DB ecrasent les defaults
        // pour les champs ou un admin a custom. Garantit qu un champ tout
        // nouveau visible/editable par defaut le reste cote prestataire sans
        // attendre que l admin clique "Save" sur /parametres/droits-des-champs.
        const map: PermissionMap = {};
        const defaults = buildDefaultPermissions();
        for (const [fieldKey, rolePerms] of Object.entries(defaults)) {
          const perm = rolePerms[role];
          if (perm) {
            map[fieldKey] = { can_see: perm.voir, can_edit: perm.modifier };
          }
        }
        if (data && data.length > 0) {
          for (const row of data) {
            map[row.field_key] = { can_see: row.can_see, can_edit: row.can_edit };
          }
        }
        cache[role] = map;
        setPermissions(map);
        setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [role, version]);

  function canSee(fieldKey: string): boolean {
    if (role === "admin") return true;
    // Pendant le chargement : afficher tous les champs (évite les sections vides)
    if (isLoading) return true;
    // Clés structurelles (__tab_*, __section_*) : absence de ligne = visible.
    // Elles n'ont jamais de défaut dans buildDefaultPermissions() (registre de
    // champs réels uniquement) — sans ce cas particulier, tout onglet/bloc
    // nouvellement rendu masquable serait invisible pour tout le monde tant
    // qu'un admin n'a pas explicitement enregistré "visible" pour chaque rôle.
    if ((fieldKey.startsWith("__tab_") || fieldKey.startsWith("__section_")) && !(fieldKey in permissions)) return true;
    return permissions[fieldKey]?.can_see ?? false;
  }

  function canEdit(fieldKey: string): boolean {
    if (role === "admin") return true;
    if (isLoading) return false; // lecture seule pendant le chargement
    return permissions[fieldKey]?.can_edit ?? false;
  }

  return { canSee, canEdit, isLoading };
}
